import React, { useState, useEffect } from 'react';
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import usePlanFeatures from '../hooks/usePlanFeatures';
import {
  LayoutDashboard, FileText, Calendar,
  LogOut, ChevronLeft, ChevronRight, Wallet, FolderOpen, Settings as SettingsIcon, BarChart3, GraduationCap, LifeBuoy,
  HardHat, Menu, X, Briefcase, Lock, MessageSquare, Send, Sparkles,
  MessagesSquare, BadgeDollarSign, UserPlus, Users,
} from 'lucide-react';
import NotificationBell from './NotificationBell';
import useAiPrefs from '../hooks/useAiPrefs';
import api from '../lib/api';
import { listKnownAccounts, forgetAccount } from '../utils/knownAccounts';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from './ui/dropdown-menu';

// Each nav item declares the plan-feature key it requires. Items WITHOUT
// `feature` (Dashboard, Estimator) are part of every plan. Items the
// caller's plan doesn't unlock are kept VISIBLE but rendered with a
// lock icon + reduced opacity — clicking them takes the user to the
// route's PlanGate upgrade screen so they can see exactly what's
// behind the gate and which plan unlocks it.
//
// NOTE: Mileage is NOT a top-level nav item — it lives as a tab inside
// the Books page (BookkeepingPage), so it's reached via /bookkeeping?tab=mileage.
const navItems = [
  { path: '/app',         icon: LayoutDashboard, label: 'Dashboard',  short: 'Home' },
  { path: '/projects',    icon: Briefcase,       label: 'Projects',   short: 'Projects' },
  // Unified Chat + Work Orders hub. Chat is the default tab; Work
  // Orders is a sibling tab inside the same page.
  { path: '/work',        icon: MessagesSquare,  label: 'Work',       short: 'Work', badgeKey: 'chat_unread' },
  { path: '/messages',    icon: Send,            label: 'Messages',   short: 'SMS',      badgeKey: 'sms_unread' },
  // Documents is now folded into Projects (top-of-page tab strip).
  // Left here as a comment so nobody re-adds it by reflex.
  { path: '/schedule',    icon: Calendar,        label: 'Schedule',   short: 'Schedule', feature: 'schedule' },
  { path: '/documents',   icon: FileText,        label: 'Documents',  short: 'Docs',     feature: 'documents' },
  { path: '/bookkeeping', icon: Wallet,          label: 'Books',      short: 'Books',    feature: 'bookkeeping' },
  { path: '/analytics',   icon: BarChart3,       label: 'Analytics',  short: 'Stats',    feature: 'analytics' },
  { path: '/payroll',     icon: BadgeDollarSign, label: 'Payroll',    short: 'Pay',      feature: 'payroll', adminOnly: true },
];

const LOGO_URL = "/mantle-logo.png";

export default function Sidebar({ collapsed, setCollapsed }) {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { has: hasFeature, loading: featuresLoading } = usePlanFeatures();
  const [chatUnread, setChatUnread] = useState(0);
  const [smsUnread, setSmsUnread] = useState(0);
  // Ask Mantle button visibility (mobile drawer only). Mirrors the same
  // gates the desktop floating button uses in AppShell so we don't show
  // it when the plan doesn't include AI or when the owner disabled it.
  const ai = useAiPrefs();
  const aiAllowed = ai.enabled('ask_mantle');

  const openAskMantle = () => {
    setDrawerOpen(false);
    // Slight delay so the drawer close animation doesn't fight the panel
    // open. AppShell listens for this event and opens AskMantlePanel.
    setTimeout(() => window.dispatchEvent(new CustomEvent('mantle:open-ai')), 120);
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  // Switch to a different known account without the two-tap
  // "sign out → then sign in" dance. We log out cleanly (revokes the
  // current tokens + clears in-memory caches) and land on /login with
  // the target username pre-filled. Passwords are NEVER cached, so the
  // user still enters theirs — this is a convenience roster, not SSO.
  const handleSwitchAccount = async (username = '') => {
    await logout();
    const qs = username ? `?u=${encodeURIComponent(username)}` : '';
    navigate(`/login${qs}`);
  };

  // Poll chat + SMS unread badges — cheap, drives the nav pips.
  useEffect(() => {
    let cancel = false;
    let timer;
    const fetchUnread = async () => {
      try {
        const [chat, sms] = await Promise.all([
          api.get('/chat/unread-count').catch(() => ({ data: {} })),
          api.get('/messages/analytics').catch(() => ({ data: {} })),
        ]);
        if (!cancel) {
          setChatUnread(chat.data?.count || 0);
          setSmsUnread(sms.data?.unread || 0);
        }
      } catch (_) { /* silent */ }
    };
    if (user) {
      fetchUnread();
      timer = setInterval(fetchUnread, 20000);
    }
    return () => { cancel = true; if (timer) clearInterval(timer); };
  }, [user, location.pathname]);

  const badgeFor = (item) => {
    if (item.badgeKey === 'chat_unread') return chatUnread;
    if (item.badgeKey === 'sms_unread') return smsUnread;
    return 0;
  };

  // Visible nav items: ALL items the user's role permits. Plan-locked
  // items are kept in the list and rendered with a lock indicator
  // (see `isLocked` calc below) instead of being hidden — that way the
  // user can see what the next tier unlocks and tap through to the
  // upgrade screen.
  const userRole = (user?.role || '').toLowerCase();
  // Field techs (crew / helper / field_tech) only see Chat in the app
  // sidebar — everything else is admin/owner territory. Their primary
  // work surface is /my-workorder or /timeclock (standalone routes
  // outside AppShell). Chat is the ONE AppShell feature they get.
  const isFieldTech = ['field_tech', 'crew', 'helper'].includes(userRole);
  const visibleNavItems = navItems.filter(item => {
    if (isFieldTech) return item.path === '/work';
    if (item.adminOnly && userRole !== 'owner' && userRole !== 'admin') return false;
    return true;
  });
  const lockedFor = (item) => !!item.feature && !featuresLoading && !hasFeature(item.feature);

  // Auto-close the drawer when the route changes (i.e. user tapped a nav item).
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Lock body scroll while the drawer is open so the underlying page can't move.
  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [drawerOpen]);

  // Custom active detection — the /work hub has two nav items (Chat,
  // Workorders) that share the SAME pathname (/work) and are only
  // distinguished by `?tab=…`. React Router's default NavLink match-by-
  // pathname would light both up on /work, so we compute active
  // manually by parsing the query.
  const isItemActive = (itemPath) => {
    const [p, q] = itemPath.split('?');
    if (p === '/') return location.pathname === '/';
    if (!location.pathname.startsWith(p)) return false;
    if (!q) return true;
    const wanted = new URLSearchParams(q);
    const actual = new URLSearchParams(location.search);
    for (const [k, v] of wanted.entries()) {
      if ((actual.get(k) || (k === 'tab' ? 'chat' : '')) !== v) return false;
    }
    return true;
  };

  // Find the active nav item so we can show its label in the top bar.
  const activeItem = visibleNavItems.find(n => isItemActive(n.path)) || visibleNavItems[0];

  return (
    <>
      {/* ============================================================== */}
      {/* MOBILE: Top bar — theme-aware (light bg in light mode, dark bg in dark mode) */}
      {/* ============================================================== */}
      {/* iOS safe-area handling: `viewport-fit=cover` (set in index.html
          so the composer can lift above the home indicator) also lets
          our content extend UP into the status-bar zone. We compensate
          here by:
            - padding-top = safe-area-inset-top (34–44px on notched iPhones,
              20px on older iPhones, 0 on Android/desktop)
            - height = h-14 + safe-area-inset-top (so the bar stays 56px
              of TAP area beneath the phone's status bar)
          Without this the hamburger/title render UNDER the notch. */}
      <div
        className="md:hidden fixed top-0 left-0 right-0 bg-white/95 dark:bg-[#0D1520]/95 backdrop-blur-xl border-b border-slate-200 dark:border-white/[0.04] z-40 flex items-center justify-between px-2"
        style={{
          paddingTop: 'env(safe-area-inset-top, 0px)',
          height: 'calc(3.5rem + env(safe-area-inset-top, 0px))',
        }}
      >
        <button
          data-testid="open-mobile-drawer"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open menu"
          className="h-11 w-11 flex items-center justify-center rounded-lg text-slate-900 dark:text-white active:scale-95 active:bg-slate-100 dark:active:bg-white/[0.06] transition-all"
        >
          <Menu size={22} strokeWidth={2} />
        </button>

        {/* Current page title */}
        <div className="flex-1 flex items-center justify-center pointer-events-none gap-2 min-w-0 px-2">
          <activeItem.icon size={15} className="text-[#B98A2C] flex-shrink-0" strokeWidth={2.2} />
          <span className="text-sm font-semibold text-slate-900 dark:text-white tracking-tight truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {activeItem.label}
          </span>
        </div>

        <div className="flex items-center gap-1">
          <NotificationBell variant="topbar" />
          <button
            data-testid="mobile-logout"
            onClick={handleLogout}
            aria-label="Sign out"
            className="h-11 w-11 flex items-center justify-center rounded-lg text-slate-600 dark:text-[#A8B8CA] hover:text-red-500 active:bg-red-500/10 active:scale-95 transition-all"
          >
            <LogOut size={20} strokeWidth={1.9} />
          </button>
        </div>
      </div>

      {/* ============================================================== */}
      {/* MOBILE: Slide-in drawer                                         */}
      {/* ============================================================== */}
      <div
        data-testid="drawer-backdrop"
        onClick={() => setDrawerOpen(false)}
        className={`md:hidden fixed inset-0 z-40 bg-black/40 dark:bg-black/55 backdrop-blur-sm transition-opacity duration-200 ${drawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      />
      <aside
        data-testid="mobile-drawer"
        aria-hidden={!drawerOpen}
        className={`md:hidden fixed top-0 left-0 bottom-0 z-50 w-[78%] max-w-[300px] bg-white dark:bg-[#0D1520] border-r border-slate-200 dark:border-white/[0.06] shadow-2xl flex flex-col transition-transform duration-250 ease-out ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}`}
        // Same safe-area handling as the mobile topbar — the drawer
        // covers the top of the screen when open so its logo/close
        // button must clear the iOS status bar too.
        style={{ willChange: 'transform', paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        {/* Drawer header — keep the logo on a small dark tile in light mode
            so the dark-on-transparent Mantle "M" stays visible. */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-white/[0.06]">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-[#0D1520] dark:bg-transparent flex items-center justify-center flex-shrink-0">
              <img src={LOGO_URL} alt="Mantle" className="w-8 h-8 object-contain" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-slate-900 dark:text-white tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Mantle</p>
              <p className="text-[9px] text-[#B98A2C] font-semibold tracking-[0.18em] uppercase">Suite</p>
            </div>
          </div>
          <button
            data-testid="close-mobile-drawer"
            onClick={() => setDrawerOpen(false)}
            aria-label="Close menu"
            className="h-10 w-10 flex items-center justify-center rounded-lg text-slate-500 dark:text-[#A8B8CA] hover:bg-slate-100 dark:hover:bg-white/[0.05] active:scale-95"
          >
            <X size={20} />
          </button>
        </div>

        {/* User card */}
        {user && (
          <div className="px-4 py-3 border-b border-slate-200 dark:border-white/[0.04] flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#CBA33B] to-[#B98A2C] flex items-center justify-center text-white font-bold text-base flex-shrink-0 shadow-sm">
              {(user.name || user.username || '?').charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{user.name || user.username}</p>
              <p className="text-[10px] text-[#B98A2C] font-semibold tracking-wider uppercase truncate">{user.role || 'member'}</p>
            </div>
          </div>
        )}

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1">
          {visibleNavItems.map((item) => {
            const locked = lockedFor(item);
            const isActive = isItemActive(item.path);
            const activeClass = 'bg-[#B98A2C]/12 text-[#B98A2C] dark:text-[#CBA33B]';
            const idleClass = locked
              ? 'text-slate-400 dark:text-[#5E6B7C] hover:bg-slate-100 dark:hover:bg-white/[0.03]'
              : 'text-slate-700 dark:text-[#C8D2DE] hover:bg-slate-100 dark:hover:bg-white/[0.04] hover:text-slate-900 dark:hover:text-white';
            return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}${locked ? '-locked' : ''}`}
              style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
              className={`flex items-center gap-3 px-3 py-3 rounded-xl text-[14px] font-medium transition-all active:scale-[0.98] ${isActive ? activeClass : idleClass}`}
            >
              <item.icon size={20} strokeWidth={isActive ? 2.2 : 1.8} className="flex-shrink-0" />
              <span className="truncate flex-1">{item.label}</span>
              {badgeFor(item) > 0 && (
                <span data-testid={`nav-badge-${item.label.toLowerCase()}`} className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-[#CBA33B] text-[#0D1520] text-[10px] font-black tabular-nums">
                  {badgeFor(item) > 99 ? '99+' : badgeFor(item)}
                </span>
              )}
              {locked ? <Lock size={13} className="text-slate-400 dark:text-[#5E6B7C] flex-shrink-0" />
                      : (isActive && badgeFor(item) === 0 && <span className="w-1.5 h-1.5 rounded-full bg-[#B98A2C]" />)}
            </Link>
          );})}
        </nav>

        {/* Bottom actions — the Notification bell, Settings link, and
            Help & Tutorials link are HIDDEN for field techs (crew/helper/
            field_tech). They only need Chat + Work Orders + Timeclock. */}
        <div className="border-t border-slate-200 dark:border-white/[0.06] p-2 space-y-1">
          {!isFieldTech && aiAllowed && (
            <button
              onClick={openAskMantle}
              data-testid="drawer-ask-mantle"
              style={{ WebkitTapHighlightColor: 'transparent' }}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-[14px] font-bold bg-gradient-to-r from-[#CBA33B] to-[#B98A2C] text-[#0D1520] shadow-md shadow-[#B98A2C]/20 hover:brightness-110 active:scale-[0.98] transition-all"
            >
              <Sparkles size={20} strokeWidth={2.2} className="flex-shrink-0" />
              <span className="flex-1 text-left">Ask Mantle</span>
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            </button>
          )}
          {!isFieldTech && <NotificationBell variant="drawer-row" />}
          {!isFieldTech && (
            <NavLink
              to="/settings"
              data-testid="nav-settings-mobile"
              style={{ WebkitTapHighlightColor: 'transparent' }}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-3 rounded-xl text-[14px] font-medium transition-all active:scale-[0.98] ${
                isActive
                  ? 'bg-[#B98A2C]/12 text-[#B98A2C] dark:text-[#CBA33B]'
                  : 'text-slate-700 dark:text-[#C8D2DE] hover:bg-slate-100 dark:hover:bg-white/[0.04]'
              }`}
            >
              <SettingsIcon size={20} strokeWidth={1.8} className="flex-shrink-0" />
              <span className="flex-1">Settings</span>
            </NavLink>
          )}
          {!isFieldTech && (
            <NavLink
              to="/help"
              data-testid="nav-help-mobile"
              style={{ WebkitTapHighlightColor: 'transparent' }}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-3 rounded-xl text-[14px] font-medium transition-all active:scale-[0.98] ${
                isActive
                  ? 'bg-[#B98A2C]/12 text-[#B98A2C] dark:text-[#CBA33B]'
                  : 'text-slate-700 dark:text-[#C8D2DE] hover:bg-slate-100 dark:hover:bg-white/[0.04]'
              }`}
            >
              <GraduationCap size={20} strokeWidth={1.8} className="flex-shrink-0" />
              <span className="flex-1">Help &amp; Tutorials</span>
            </NavLink>
          )}
          <NavLink to="/feedback" data-testid="nav-feedback-mobile"
            style={{ WebkitTapHighlightColor: 'transparent' }}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-3 rounded-xl text-[14px] font-semibold transition-all active:scale-[0.98] ${
              isActive ? 'bg-red-500/15 text-red-300' : 'text-red-400 hover:bg-red-500/10'
            }`}>
            <LifeBuoy size={20} strokeWidth={1.9} className="flex-shrink-0" />
            <span className="flex-1">Feedback &amp; Support</span>
          </NavLink>
          <button
            onClick={handleLogout}
            data-testid="drawer-logout"
            style={{ WebkitTapHighlightColor: 'transparent' }}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-[14px] font-medium text-slate-700 dark:text-[#C8D2DE] hover:text-red-500 hover:bg-red-500/8 active:scale-[0.98] transition-all"
          >
            <LogOut size={20} strokeWidth={1.8} className="flex-shrink-0" />
            <span className="flex-1 text-left">Sign Out</span>
          </button>
        </div>
      </aside>

      {/* ============================================================== */}
      {/* DESKTOP: Persistent side panel (unchanged)                      */}
      {/* ============================================================== */}
      <aside data-testid="app-sidebar"
        className={`hidden md:flex fixed left-0 top-0 h-full bg-[#0D1520] border-r border-white/[0.04] z-40 transition-all duration-300 flex-col ${collapsed ? 'w-[68px]' : 'w-[220px]'}`}>
        <div className={`flex items-center gap-2.5 border-b border-white/[0.04] ${collapsed ? 'p-3 justify-center' : 'px-4 py-3.5'}`}>
          <img src={LOGO_URL} alt="Mantle" className="w-9 h-9 object-contain flex-shrink-0" />
          {!collapsed && (
            <div className="overflow-hidden">
              <h2 className="text-sm font-bold text-white tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Mantle</h2>
              <p className="text-[9px] text-[#B98A2C] font-semibold tracking-[0.15em] uppercase">Suite</p>
            </div>
          )}
        </div>

        <nav className="flex-1 py-3 px-2 overflow-y-auto">
          {(() => {
            // Grouped navigation — items keep their original order but
            // sit in visual clusters divided only by whitespace + a
            // hairline. Captions are gone (kept the sidebar reading
            // like a heavy TOC), so the nav feels light and quick to
            // scan while still telegraphing "these belong together".
            const groupOrder = [
              { key: 'workspace',     paths: ['/app', '/projects'] },
              { key: 'communication', paths: ['/work', '/messages'] },
              { key: 'operations',    paths: ['/schedule', '/documents'] },
              { key: 'finance',       paths: ['/bookkeeping', '/analytics', '/payroll'] },
            ];
            const shownGroups = groupOrder
              .map(g => ({ ...g, items: visibleNavItems.filter(it => g.paths.includes(it.path)) }))
              .filter(g => g.items.length > 0);

            const renderItem = (item) => {
              const locked = lockedFor(item);
              const isActive = isItemActive(item.path);
              const activeClass = 'bg-[#B98A2C]/10 text-[#CBA33B] font-semibold';
              const idleClass = locked
                ? 'text-[#5E6B7C] hover:text-[#8899AA] hover:bg-white/[0.02]'
                : 'text-[#8899AA] hover:text-white hover:bg-white/[0.03]';
              return (
                <Link key={item.path} to={item.path} data-testid={`nav-${item.label.toLowerCase()}-desktop${locked ? '-locked' : ''}`}
                  className={`relative flex items-center gap-2.5 px-3 py-[7px] rounded-md text-[13px] transition-all duration-200 group ${isActive ? activeClass : idleClass}`}>
                  {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 rounded-r-full bg-[#B98A2C]" />}
                  <item.icon size={17} className="flex-shrink-0" strokeWidth={isActive ? 2.2 : 1.75} />
                  {!collapsed && <span className="truncate flex-1">{item.label}</span>}
                  {!collapsed && locked && <Lock size={11} className="text-[#5E6B7C] flex-shrink-0" />}
                  {badgeFor(item) > 0 && (
                    <span data-testid={`nav-badge-desktop-${item.label.toLowerCase()}`} className={`inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-[#CBA33B] text-[#0D1520] text-[9px] font-black tabular-nums ${collapsed ? 'absolute -top-0.5 -right-0.5' : ''}`}>
                      {badgeFor(item) > 99 ? '99+' : badgeFor(item)}
                    </span>
                  )}
                </Link>
              );
            };

            return shownGroups.map((g, gi) => (
              <div
                key={g.key}
                className={`space-y-0.5 ${gi > 0 ? 'mt-2 pt-2 border-t border-white/[0.03]' : ''}`}
              >
                {g.items.map(renderItem)}
              </div>
            ));
          })()}
        </nav>

        <div className="border-t border-white/[0.04] p-2.5 space-y-2">
          {!collapsed && user && (
            <div className="flex items-center gap-2.5 px-3 py-2 rounded-md bg-white/[0.02]">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#B98A2C] to-[#8A6820] flex items-center justify-center text-white text-[11px] font-black flex-shrink-0" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {((user.name || user.username || '?').charAt(0) || '?').toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-white truncate font-semibold leading-tight">{user.name || user.username}</p>
                <p className="text-[9px] text-[#758A9F] truncate leading-tight">Signed in</p>
              </div>
            </div>
          )}
          {/* Utility row — Notifications, Settings, Help. Hidden for
              field techs; they get a lean sidebar with just Sign Out. */}
          {!isFieldTech && (
            <div className="space-y-0.5">
              {!collapsed ? (
                <NotificationBell variant="desktop-row" />
              ) : (
                <div className="flex justify-center"><NotificationBell variant="desktop-icon" /></div>
              )}
              <NavLink to="/settings" data-testid="nav-settings-desktop"
                className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 w-full rounded-md text-[13px] transition-all ${
                  isActive ? 'bg-[#B98A2C]/10 text-[#CBA33B] font-semibold' : 'text-[#8899AA] hover:text-white hover:bg-white/[0.03]'
                }`}>
                <SettingsIcon size={18} className="flex-shrink-0" strokeWidth={1.8} />{!collapsed && <span>Settings</span>}
              </NavLink>
              <NavLink to="/help" data-testid="nav-help-desktop"
                title="Help & Tutorials"
                className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 w-full rounded-md text-[13px] transition-all ${
                  isActive ? 'bg-[#B98A2C]/10 text-[#CBA33B] font-semibold' : 'text-[#8899AA] hover:text-white hover:bg-white/[0.03]'
                }`}>
                <GraduationCap size={18} className="flex-shrink-0" strokeWidth={1.8} />{!collapsed && <span>Help</span>}
              </NavLink>
              <NavLink to="/feedback" data-testid="nav-feedback-desktop"
                title="Feedback & Support — report a bug or suggest a feature"
                className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 w-full rounded-md text-[13px] font-semibold transition-all ${
                  isActive ? 'bg-red-500/15 text-red-300' : 'text-red-400/90 hover:text-red-300 hover:bg-red-500/10'
                }`}>
                <LifeBuoy size={18} className="flex-shrink-0" strokeWidth={1.9} />{!collapsed && <span>Feedback</span>}
              </NavLink>
            </div>
          )}
          {/* Danger zone — Switch Account + Sign Out separated from the
              utility row by a hairline. Switch Account uses a dropdown
              of known accounts on this device (usernames only — no
              password caching); tap one and we log out + redirect to
              /login with that user prefilled. */}
          <div className="pt-2 border-t border-white/[0.04] space-y-0.5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="switch-account-btn"
                  title="Switch to another account"
                  className="flex items-center gap-2.5 px-3 py-2 w-full rounded-md text-[13px] text-[#8899AA] hover:text-[#CBA33B] hover:bg-white/[0.03] transition-all"
                >
                  <Users size={18} className="flex-shrink-0" strokeWidth={1.8} />
                  {!collapsed && <span className="flex-1 text-left">Switch Account</span>}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                data-testid="switch-account-menu"
                side="right" align="end" sideOffset={8}
                className="min-w-[220px] bg-[#0D1520] border border-white/[0.06] text-[#CFDBE8]"
              >
                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.14em] text-[#758A9F] font-bold">
                  Recent accounts
                </DropdownMenuLabel>
                {(() => {
                  const known = listKnownAccounts().filter(a => a.username !== (user?.username));
                  if (known.length === 0) {
                    return (
                      <div className="px-2 py-2 text-[12px] text-[#758A9F]">No other accounts on this device yet.</div>
                    );
                  }
                  return known.map((acc) => (
                    <DropdownMenuItem
                      key={acc.username}
                      data-testid={`switch-account-item-${acc.username}`}
                      onSelect={() => handleSwitchAccount(acc.username)}
                      className="flex items-center gap-2.5 cursor-pointer focus:bg-white/[0.05] focus:text-white"
                    >
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#B98A2C]/70 to-[#8A6820]/70 flex items-center justify-center text-white text-[10px] font-black flex-shrink-0" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                        {((acc.name || acc.username).charAt(0) || '?').toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[12.5px] font-semibold truncate leading-tight">{acc.name || acc.username}</p>
                        {acc.workspace_name && (
                          <p className="text-[10px] text-[#758A9F] truncate leading-tight">{acc.workspace_name}</p>
                        )}
                      </div>
                    </DropdownMenuItem>
                  ));
                })()}
                <DropdownMenuSeparator className="bg-white/[0.06]" />
                <DropdownMenuItem
                  data-testid="switch-account-add-btn"
                  onSelect={() => handleSwitchAccount('')}
                  className="flex items-center gap-2.5 cursor-pointer focus:bg-white/[0.05] focus:text-white"
                >
                  <UserPlus size={14} className="text-[#CBA33B]" />
                  <span className="text-[12.5px]">Sign in with another account</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button data-testid="logout-button" onClick={handleLogout}
              className="flex items-center gap-2.5 px-3 py-2 w-full rounded-md text-[13px] text-[#8899AA] hover:text-red-400 hover:bg-red-500/5 transition-all">
              <LogOut size={18} className="flex-shrink-0" />{!collapsed && <span>Sign Out</span>}
            </button>
          </div>
          <button data-testid="toggle-sidebar" onClick={() => setCollapsed(!collapsed)}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="flex items-center justify-center w-full py-1 rounded-md text-[#758A9F] hover:text-[#CBA33B] hover:bg-white/[0.03] transition-colors">
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>
      </aside>
    </>
  );
}
